import React, { useState, useEffect, useMemo } from "react";
// +++ NEW: Import useFieldArray and Controller for the modal form +++
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { DateRange } from "react-date-range";
import { addDays } from "date-fns";
import { API_BASE_URL } from "../utils/constant";

import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import Alsol_QR from "../assets/form/alsol_qr.jpg";
import { useAuth } from "../context/AuthContext";
import cityStateData from "../utils/cities";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";

// +++ NEW: A simple modal component for creating custom services +++
// You can move this to its own file (e.g., CustomServiceModal.js) if you prefer
const CustomServiceModal = ({ category, onClose, onSubmit }) => {
  const { register, handleSubmit, control, watch } = useForm({
    defaultValues: {
      name: "",
      price: "",
      plan_type: category === "marketing" ? "Monthly" : null,
      addons: [],
    },
  });
  const { fields, append, remove } = useFieldArray({
    control,
    name: "addons",
  });

  const onSubmitForm = (data) => {
    onSubmit(data);
    onClose();
  };

  const inputClass = "w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition duration-200 ease-in-out";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white p-6 rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()} // Prevent modal close on content click
      >
        <h3 className="text-2xl font-bold mb-5 text-blue-800">
          Add Custom {category} Service
        </h3>
        <form onSubmit={handleSubmit(onSubmitForm)} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Service Name *</label>
            <input
              {...register("name", { required: "Name is required" })}
              className={inputClass}
              placeholder="e.g., Custom SEO Package"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Base Price (₹) *</label>
            <input
              type="number"
              {...register("price", { required: "Price is required", valueAsNumber: true, min: 0 })}
              className={inputClass}
              placeholder="e.g., 5000"
            />
          </div>

          {/* Only show plans for Marketing category */}
          {category === "marketing" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Plan Type *</label>
              <select
                {...register("plan_type", { required: true })}
                className={`${inputClass} bg-white`}
              >
                <option value="Monthly">Monthly</option>
                <option value="Quarterly">Quarterly</option>
                <option value="Annually">Annually</option>
              </select>
            </div>
          )}

          {/* Custom Addons */}
          <div className="pt-2">
            <h4 className="text-lg font-semibold text-gray-800">Custom Add-ons</h4>
            <div className="space-y-3 mt-2 max-h-40 overflow-y-auto pr-2">
              {fields.map((field, index) => (
                <div key={field.id} className="flex items-center gap-2 p-3 bg-gray-50 border border-gray-200 rounded-md">
                  <input
                    {...register(`addons.${index}.name`, { required: true })}
                    placeholder="Addon Name"
                    className="flex-1 p-2 border border-gray-300 rounded-md"
                  />
                  <input
                    type="number"
                    {...register(`addons.${index}.price`, { required: true, valueAsNumber: true, min: 0 })}
                    placeholder="Price (₹)"
                    className="w-1/3 p-2 border border-gray-300 rounded-md"
                  />
                  <button
                    type="button"
                    onClick={() => remove(index)}
                    className="text-red-500 hover:text-red-700 font-bold text-2xl"
                  >
                    &times;
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={() => append({ name: "", price: "" })}
              className="mt-2 text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              + Add Custom Addon
            </button>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t mt-5">
            <button
              type="button"
              onClick={onClose}
              className="py-2 px-4 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
            >
              Add Service
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
// +++ END of modal component +++


export default function ApplicationForm() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [range, setRange] = useState([
    {
      startDate: new Date(),
      endDate: addDays(new Date(), 7),
      key: "selection",
    },
  ]);

  // Add this near your other useState definitions
  const [formattedPartialAmount, setFormattedPartialAmount] = useState("");
  const [selectedServices, setSelectedServices] = useState([]);
  const [businessType, setBusinessType] = useState("");
  const [companyType, setCompanyType] = useState("");
  const { user } = useAuth();
  const uniqueStates = useMemo(() => [...new Set(cityStateData.map(item => item.state))], []);
  const [selectedState, setSelectedState] = useState("");
  const [filteredCities, setFilteredCities] = useState([]);
  const [customStateEnabled, setCustomStateEnabled] = useState(false);
  const [customCityEnabled, setCustomCityEnabled] = useState(false);
  const [serviceOptions, setServiceOptions] = useState({
    google: [],
    marketing: [],
    website: [],
  });
  const navigate = useNavigate();

  // +++ NEW: State for the custom service modal +++
  const [modalInfo, setModalInfo] = useState({ isOpen: false, category: null });

  // --- FORM HOOK SETUP ---
  // ✅ CHANGE 1: Switched to 'onChange' mode and added 'dirtyFields' for live validation.
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors, dirtyFields },
    trigger,
    unregister,
  } = useForm({
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const paymentType = watch("paymentType");
  const paymentStatus = watch("payment_status");
  const partialAmountValue = watch("partialAmount");

  // ✅ CHANGE 2: Create a single, memoized source of truth for the total amount.
  // This correctly calculates service price + GST, adds the already-inclusive addon price, and rounds the result.
  // This logic is still correct, as custom/existing services
  // both have `price` and `selectedAddons` properties.
  const grandTotal = useMemo(() => {
    const total = selectedServices.reduce((total, service) => {
      const serviceWithGst = service.price * 1.18;
      const addonsTotal = (service.selectedAddons || []).reduce(
        (sum, addon) => sum + addon.price, 0
      );
      return total + serviceWithGst + addonsTotal;
    }, 0);
    return Math.round(total);
  }, [selectedServices]);


  function useWindowSize() {
    const [width, setWidth] = useState(window.innerWidth);
    useEffect(() => {
      const handleResize = () => setWidth(window.innerWidth);
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, []);
    return width;
  }
  const windowWidth = useWindowSize();
  const isMobile = windowWidth < 768;


  const formatDateForInput = (date) => {
    if (!date) return '';
    return date.toISOString().split('T')[0];
  };

  // ✅ CHANGE 3: Simplified useEffect to use the correct 'grandTotal' instead of recalculating.
  // This useEffect is unchanged and correct
  useEffect(() => {
    if (paymentType === 'Cheque') {
      if (paymentStatus === 'Complete') {
        setValue('totalAmount', grandTotal, { shouldValidate: true });
      } else if (paymentStatus === 'Partial') {
        setValue('totalAmount', partialAmountValue || '', { shouldValidate: true });
      } else {
        setValue('totalAmount', '', { shouldValidate: true });
      }
    }
  }, [paymentStatus, partialAmountValue, grandTotal, paymentType, setValue]);


  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/services`);
        setServiceOptions(response.data);
      } catch (error) {
        console.error("Failed to fetch services:", error);
      }
    };
    fetchServices();
  }, []);

  // This useEffect is unchanged and correct
  useEffect(() => {
    if (selectedState) {
      const cities = cityStateData
        .filter(item => item.state === selectedState)
        .map(item => item.city);
      setFilteredCities(cities);
      setValue("state", selectedState, { shouldValidate: true });
      setValue("city", "", { shouldValidate: true });
    } else {
      setFilteredCities([]);
    }
  }, [selectedState, setValue]);

  // This useEffect is unchanged and correct
  useEffect(() => {
    const start = range[0].startDate?.toLocaleDateString("en-GB");
    const end = range[0].endDate?.toLocaleDateString("en-GB");
    setValue("photoshootDate", `${start} — ${end}`);
  }, [range, setValue]);

  
  // --- REMOVED: `useEffect` that registered/triggered the "services" field ---
  // We now validate this manually in `onSubmit`


  // This useEffect is unchanged and correct
  useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = `
        .rdrMonth { width: 25rem !important; }
        .rdrDayNumber { overflow: visible !important; }
      `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);


  // This useEffect is unchanged and correct
  useEffect(() => {

    const isPartial = paymentStatus === 'Partial';

    register("partialAmount", {

      required: isPartial ? "Partial amount is required." : false,
      valueAsNumber: true,

      min: isPartial ? { value: 1, message: "Amount must be greater than 0." } : undefined,
      max: isPartial ? { value: grandTotal, message: `Amount cannot exceed total of ₹${grandTotal.toLocaleString()}` } : undefined
    });
  }, [register, grandTotal, paymentStatus]);

  // This function is unchanged and correct
  const handlePartialAmountChange = (e) => {

    const rawValue = e.target.value.replace(/,/g, '');

    if (!/^\d*$/.test(rawValue)) return;
    const numericValue = rawValue === '' ? '' : parseInt(rawValue, 10);

    setValue('partialAmount', numericValue, { shouldValidate: true });

    setFormattedPartialAmount(numericValue === '' ? '' : numericValue.toLocaleString('en-IN'));
  };

  // --- UPDATED: `isGoogleServiceSelected` checks by category now ---
  const isGoogleServiceSelected = useMemo(() =>
    selectedServices.some(s => s.category === 'google')
    , [selectedServices]);

    useEffect(() => {
    if (!isGoogleServiceSelected) {
      // If no Google service is selected, remove the value
      // from the form state so it doesn't get submitted.
      unregister("photoshootDate");
    }
  }, [isGoogleServiceSelected, unregister]);

  // +++ NEW: Handler for the custom service modal submission +++
  const handleCustomServiceSubmit = (data) => {
    const { name, price, plan_type, addons } = data;

    // Format the custom service to match the structure in selectedServices
    const customService = {
      label: name, // Use `label` for UI consistency
      price: price,
      category: modalInfo.category,
      is_custom: true, // <-- The important flag
      plan_type: modalInfo.category === "marketing" ? plan_type : null,
      selectedAddons: addons.map(addon => ({
        name: addon.name,
        price: addon.price,
        is_custom: true, // <-- Flag for addons
      })),
    };

    // Handle single-select logic for Google and Marketing
    if (modalInfo.category === "google" || modalInfo.category === "marketing") {
      const updated = selectedServices.filter(
        (s) => s.category !== modalInfo.category
      );
      setSelectedServices([...updated, customService]);
    } else {
      // Handle multi-select logic for Website
      setSelectedServices([...selectedServices, customService]);
    }
  };


  // --- UPDATED: The entire onSubmit function ---
  const onSubmit = async (data) => {
    // +++ NEW: Manual validation for services +++
    if (selectedServices.length === 0) {
      alert("Please select or create at least one service to proceed.");
      return; // Stop submission
    }

    setIsSubmitting(true);
    try {
      const formData = new FormData();
      const finalData = { ...data }; // `data` contains all registered form fields


      finalData.total_amount = grandTotal;

      if (user && user.id) {
        formData.append("user_id", user.id);
      }

      if (finalData.businessType === "Others" && finalData.customBusinessType) {
        finalData.businessType = finalData.customBusinessType;
      }
      if (finalData.companyType === "Others" && finalData.customCompanyType) {
        finalData.companyType = finalData.customCompanyType;
      }

      // --- NEW: Build the flexible servicesToSubmit array ---
      const servicesToSubmit = selectedServices.map((item) => {
        
        // Map addons first
        const addonsToSubmit = (item.selectedAddons || []).map(addon => {
          if (addon.is_custom) {
            // Custom addon
            return {
              is_custom: true,
              name: addon.name,
              price: addon.price
            };
          }
          // Existing addon
          return { id: addon.id }; 
        });

        // Map main service
        if (item.is_custom) {
          // Custom service
          return {
            is_custom: true,
            name: item.label, // Send `label` as `name`
            price: item.price,
            category: item.category,
            plan_type: item.plan_type || null,
            addons: addonsToSubmit
          };
        }
        
        // --- Logic for EXISTING services (from your old code) ---
        const validPlanTypes = ["Monthly", "Quarterly", "Annually"];
        let planType = null;
        // Check for plan type in the label, e.g., "EchoWave (Monthly)"
        const match = item.label.match(/\(([^)]+)\)/);
        if (match && validPlanTypes.includes(match[1])) {
          planType = match[1];
        }
        
        return {
          id: item.id, // Existing ID
          plan_type: planType,
          addons: addonsToSubmit
        };
      });
      

      // --- REMOVED: `delete finalData.services;` ---
      // `services` was never a field in `finalData` anyway, so this is safe.

      if (finalData.chequeDate && /^\d{2}\/\d{2}\/\d{4}$/.test(finalData.chequeDate)) {
        const [day, month, year] = finalData.chequeDate.split('/');
        finalData.chequeDate = `${year}-${month}-${day}`;
      }

      // Append all registered form fields to formData
      for (const key in finalData) {
        if (["screenshot", "customBusinessType", "customCompanyType"].includes(key)) continue; // Handled separately or implicitly
        
        if (Array.isArray(finalData[key])) {
          finalData[key].forEach(val => formData.append(`${key}[]`, val));
        } else if (finalData[key] !== null && finalData[key] !== undefined) {
          formData.append(key, finalData[key]);
        }
      }

      // --- REMOVED: Old `servicesToSubmit.forEach` loop that appended arrays ---

      // +++ NEW: Append services as a single JSON string +++
      // This is what our updated API controller expects
      formData.append("services", JSON.stringify(servicesToSubmit));

      // Append screenshot file
      if (data.screenshot && data.screenshot[0]) {
        formData.append("screenshot", data.screenshot[0]);
      }

      // Send the request
      const response = await fetch(`${API_BASE_URL}/api/application-form`, {
        method: "POST",
        headers: { Accept: "application/json" },
        body: formData,
      });

      // Handle response
      if (response.ok) {
        alert("Form submitted successfully!");
        reset(); // Reset react-hook-form
        setSelectedServices([]); // Reset services state
        setBusinessType("");
        setCompanyType("");
        setSelectedState("");
        setRange([{ startDate: new Date(), endDate: addDays(new Date(), 7), key: "selection" }]);
        navigate("/profile");
      } else {
        const err = await response.json();
        console.error("Server Error:", err);
        alert(`Form submission failed: ${err.message || JSON.stringify(err.errors) || 'Unknown error'}`);
      }
    } catch (error) {
      console.error("Submission error:", error);
      alert("An error occurred during submission. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };
  // --- END of updated onSubmit ---


  const inputClass = "w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition duration-200 ease-in-out";
  const errorInputClass = "border-red-500 ring-red-500 focus:border-red-500 focus:ring-red-500";
  const successInputClass = "border-green-500 ring-green-500 focus:border-green-500 focus:ring-green-500";
  const sectionTitle = "col-span-full text-2xl font-bold text-blue-700 mt-8 mb-4 border-b-2 border-blue-200 pb-2";
  const errorTextClass = "text-red-600 text-sm mt-1";


  const today = new Date();
  const maxDate = addDays(today, 90);


  return (
    <div className="max-w-6xl mx-auto mt-28 p-10 bg-white shadow-2xl rounded-3xl my-12 font-sans over">

      {/* +++ NEW: Render the modal if it's open +++ */}
      {modalInfo.isOpen && (
        <CustomServiceModal
          category={modalInfo.category}
          onClose={() => setModalInfo({ isOpen: false, category: null })}
          onSubmit={handleCustomServiceSubmit}
        />
      )}

      <div className="w-full flex justify-end mb-4">
        <Link
          to={'/profile'}
          className="inline-flex items-center gap-2 px-4 py-2 border border-blue-600 text-sm font-semibold text-blue-600 rounded-lg hover:bg-blue-50 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          View Submitted Forms
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
          </svg>
        </Link>
      </div>
      <h1 className="text-4xl font-extrabold mb-10 text-center text-blue-800 tracking-tight">
        Service Acquisition Form
      </h1>

      <form onSubmit={handleSubmit(onSubmit)} className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6 overflow-hidden">
        {/* Business Details Section */}
        <h2 className={sectionTitle}>Business Information</h2>


        <div className="col-span-1">
          <input placeholder="Business Name *" {...register("businessName", { required: "Business name is required." })} className={`${inputClass} ${errors.businessName ? errorInputClass : ''} ${!errors.businessName && dirtyFields.businessName ? successInputClass : ''}`} />
          {errors.businessName && <p className={errorTextClass}>{errors.businessName.message}</p>}
        </div>

        <div className="col-span-1">
          <input placeholder="Owner Name *" {...register("ownerName", { required: "Owner name is required." })} className={`${inputClass} ${errors.ownerName ? errorInputClass : ''} ${!errors.ownerName && dirtyFields.ownerName ? successInputClass : ''}`} />
          {errors.ownerName && <p className={errorTextClass}>{errors.ownerName.message}</p>}
        </div>

        <div className="col-span-1">
          <input type="email" placeholder="Email ID *" {...register("email", { required: "Email is required.", pattern: { value: /^\S+@\S+\.\S+$/, message: "Invalid email address." } })} className={`${inputClass} ${errors.email ? errorInputClass : ''} ${!errors.email && dirtyFields.email ? successInputClass : ''}`} />
          {errors.email && <p className={errorTextClass}>{errors.email.message}</p>}
        </div>

        <div className="col-span-1">
          <label className="block text-sm text-gray-700 mb-1">Contact No. *</label>
          <div className="flex items-center border rounded-md overflow-hidden">
            <span className="px-3 text-sm text-gray-600 bg-gray-100 border-r">+91</span>
            <input
              placeholder="10-digit mobile number"
              {...register("contactNo", {
                required: "Contact number is required.",
                pattern: {
                  value: /^[6-9]\d{9}$/,
                  message: "Please enter a valid 10-digit Indian mobile number.",
                },
              })}
              className={`flex-1 px-3 py-2 outline-none ${inputClass} ${errors.contactNo ? errorInputClass : ''} ${!errors.contactNo && dirtyFields.contactNo ? successInputClass : ''}`}
            />
          </div>
          {errors.contactNo && (
            <p className={errorTextClass}>{errors.contactNo.message}</p>
          )}
        </div>

        <div className="col-span-1">
          <label className="block text-sm text-gray-700 mb-1">Alternative Contact No.</label>
          <div className="flex items-center border rounded-md overflow-hidden">
            <span className="px-3 text-sm text-gray-600 bg-gray-100 border-r">+91</span>
            <input
              placeholder="10-digit mobile number"
              {...register("altContact", {
                pattern: {
                  value: /^[6-9]\d{9}$/,
                  message: "Please enter a valid 10-digit Indian mobile number.",
                },
              })}
              className={`flex-1 px-3 py-2 outline-none ${inputClass} ${errors.altContact ? errorInputClass : ''} ${!errors.altContact && dirtyFields.altContact ? successInputClass : ''}`}
            />
          </div>
          {errors.altContact && (
            <p className={errorTextClass}>{errors.altContact.message}</p>
          )}
        </div>


        <div className="col-span-1">
          <select {...register("businessType", { required: "Please select a business type." })} className={`${inputClass} bg-white ${errors.businessType ? errorInputClass : ''} ${!errors.businessType && dirtyFields.businessType ? successInputClass : ''}`} onChange={(e) => setBusinessType(e.target.value)} value={businessType}>
            <option value="">-- Select Business Type * --</option>
            <option value="Retail">Retail</option>
            <option value="Wholesale / Manufacturer">Wholesale / Manufacturer</option>
            <option value="Service Industry">Service Industry</option>
            <option value="Others">Others</option>
          </select>
          {errors.businessType && <p className={errorTextClass}>{errors.businessType.message}</p>}
        </div>

        {businessType === "Others" && (
          <div className="col-span-1">
            <input placeholder="Please specify your business type *" {...register("customBusinessType", { required: "This field is required when 'Others' is selected." })} className={`${inputClass} ${errors.customBusinessType ? errorInputClass : ''} ${!errors.customBusinessType && dirtyFields.customBusinessType ? successInputClass : ''}`} />
            {errors.customBusinessType && <p className={errorTextClass}>{errors.customBusinessType.message}</p>}
          </div>
        )}

        <div className="col-span-1">
          <input placeholder="Address *" {...register("address", { required: "Address is required." })} className={`${inputClass} ${errors.address ? errorInputClass : ''} ${!errors.address && dirtyFields.address ? successInputClass : ''}`} />
          {errors.address && <p className={errorTextClass}>{errors.address.message}</p>}
        </div>

        <div className="col-span-1">
          <input placeholder="Pincode *" {...register("pincode", { required: "Pincode is required.", pattern: { value: /^[1-9][0-9]{5}$/, message: "Invalid Indian Pincode." } })} className={`${inputClass} ${errors.pincode ? errorInputClass : ''} ${!errors.pincode && dirtyFields.pincode ? successInputClass : ''}`} />
          {errors.pincode && <p className={errorTextClass}>{errors.pincode.message}</p>}
        </div>

        <div className="col-span-1">
          <select
            {...register("state", { required: !customStateEnabled && "State is required." })}
            className={`${inputClass} bg-white ${errors.state ? errorInputClass : ''} ${!errors.state && dirtyFields.state && !customStateEnabled ? successInputClass : ''}`}
            value={selectedState}
            onChange={(e) => {
              const val = e.target.value;
              if (val === "__custom__") {
                setCustomStateEnabled(true);
                setSelectedState("");
                setValue("state", "", { shouldValidate: true });
              } else {
                setCustomStateEnabled(false);
                setSelectedState(val);
              }
            }}
          >
            <option value="">-- Select State * --</option>
            {uniqueStates.map((state, idx) => (
              <option key={idx} value={state}>{state}</option>
            ))}
            <option value="__custom__">-- Other (Type manually) --</option>
          </select>
          {errors.state && !customStateEnabled && <p className={errorTextClass}>{errors.state.message}</p>}
        </div>

        {customStateEnabled && (
          <div className="col-span-1">
            <input placeholder="Enter State Manually *" {...register("state", { required: "State is required." })} className={`${inputClass} ${errors.state ? errorInputClass : ''} ${!errors.state && dirtyFields.state ? successInputClass : ''}`} />
            {errors.state && <p className={errorTextClass}>{errors.state.message}</p>}
          </div>
        )}

        <div className="col-span-1">
          <select
            {...register("city", { required: !customCityEnabled && "City is required." })}
            className={`${inputClass} bg-white ${errors.city ? errorInputClass : ''} ${!errors.city && dirtyFields.city && !customCityEnabled ? successInputClass : ''}`}
            disabled={!selectedState && !customStateEnabled}
            onChange={(e) => {
              const val = e.target.value;
              if (val === "__custom__") {
                setCustomCityEnabled(true);
                setValue("city", "", { shouldValidate: true });
              } else {
                setCustomCityEnabled(false);
                setValue("city", val, { shouldValidate: true });
              }
            }}
          >
            <option value="">-- Select City * --</option>
            {filteredCities.map((city, idx) => (
              <option key={idx} value={city}>{city}</option>
            ))}
            <option value="__custom__">-- Other (Type manually) --</option>
          </select>
          {errors.city && !customCityEnabled && <p className={errorTextClass}>{errors.city.message}</p>}
        </div>


        {customCityEnabled && (
          <div className="col-span-1">
            <input placeholder="Enter City Manually *" {...register("city", { required: "City is required." })} className={`${inputClass} ${errors.city ? errorInputClass : ''} ${!errors.city && dirtyFields.city ? successInputClass : ''}`} />
            {errors.city && <p className={errorTextClass}>{errors.city.message}</p>}
          </div>
        )}

        <div className="col-span-full bg-gray-50 border border-gray-200 rounded-xl p-6 mt-4">
          <label className="text-lg font-semibold text-gray-800 mb-3 block">Description / Comments</label>
          <textarea {...register("description")} className="w-full h-32 p-4 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200 ease-in-out" placeholder="Write any specific requests, notes, or additional info here..."></textarea>
        </div>


        {/* --- REMOVED: Hidden "services" input and error --- */}


        {/* Google Services Section */}
        {/* +++ NEW: Title row with "Create Custom" button +++ */}
        <div className="col-span-full flex justify-between items-center">
          <h2 className={sectionTitle} style={{ marginTop: 0, marginBottom: 0, border: 'none', paddingBottom: 0 }}>Google Services</h2>
          <button
            type="button"
            onClick={() => setModalInfo({ isOpen: true, category: 'google' })}
            className="py-2 px-4 bg-blue-100 text-blue-700 font-semibold rounded-lg hover:bg-blue-200 transition-all text-sm"
          >
            + Create Custom
          </button>
        </div>
        <div className="col-span-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 overflow-hidden px-2 py-1">
          {serviceOptions.google.map((item, idx) => {
            // --- UPDATED: Check includes is_custom flag ---
            const isSelected = selectedServices.some(
              (s) => s.label === item.label && !s.is_custom
            );
            return (
              <div
                key={idx}
                onClick={() => {
                  if (isSelected) {
                    setSelectedServices((prev) =>
                      prev.filter((s) => s.label !== item.label)
                    );
                  } else {
                    const updated = selectedServices.filter(
                      (s) => s.category !== 'google' // Single select logic
                    );
                    // +++ NEW: Add category and is_custom flag +++
                    setSelectedServices([...updated, { ...item, category: 'google', is_custom: false, selectedAddons: [] }]);
                  }
                }}
                className={`relative cursor-pointer p-5 rounded-xl border-2 text-base font-medium transition-all duration-300 ease-in-out transform hover:scale-[1.02] hover:shadow-lg ${isSelected
                  ? "bg-blue-50 border-blue-600 shadow-md"
                  : "bg-white border-gray-300"
                  } hover:border-blue-400`}
              >
                {item.label} – ₹{item.price.toLocaleString('en-IN')}
                {isSelected && (
                  <span className="absolute top-3 right-3 text-green-600 text-xl font-bold">
                    ✓
                  </span>
                )}
              </div>
            );
          })}
        </div>

        {/* +++ NEW: Show custom Google services +++ */}
        {selectedServices.filter(s => s.category === 'google' && s.is_custom).map((item, idx) => (
            <div key={`custom-google-${idx}`} className="col-span-full sm:col-span-1 lg:col-span-1">
                <div className="relative p-5 rounded-xl border-2 text-base font-medium bg-blue-50 border-blue-600 shadow-md">
                    {item.label} – ₹{item.price.toLocaleString('en-IN')}
                    <span className="absolute top-2 right-3 text-xs text-blue-600 font-semibold">CUSTOM</span>
                    <button
                        type="button"
                        onClick={() => setSelectedServices(prev => prev.filter(s => s.label !== item.label))}
                        className="absolute top-1/2 -translate-y-1/2 right-3 text-red-500 hover:text-red-700 text-2xl font-bold"
                    >
                        &times;
                    </button>
                </div>
            </div>
        ))}


        {/* Photoshoot Schedule (Unchanged) */}
        {isGoogleServiceSelected && (
          <div className="col-span-full bg-blue-50 border border-blue-200 rounded-xl p-6 mt-6">
            <label className="text-lg font-semibold text-blue-800 flex items-center gap-3 mb-4">
              <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                <path d="M6 2a1 1 0 00-1 1v1H5a3 3 0 00-3 3v1h16V7a3 3 0 00-3-3h-.001V3a1 1 0 00-2 0v1H7V3a1 1 0 00-1-1zM2 9v7a3 3 0 003 3h10a3 3 0 003-3V9H2z" />
              </svg>
              Photoshoot Schedule <span className="text-red-500">*</span>
            </label>

            <p className="text-sm text-gray-600 mb-5">
              Select the desired start and end date for your photoshoot (up to 90 days in advance).
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
              <div className="rounded-lg border border-gray-300 shadow-md p-4">
                {isMobile ? (
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                      <input
                        type="date"
                        id="startDate"
                        className={inputClass}
                        value={formatDateForInput(range[0].startDate)}
                        min={formatDateForInput(today)}
                        max={formatDateForInput(maxDate)}
                        onChange={(e) => {
                          const newDate = e.target.value ? new Date(e.target.value) : new Date();
                          setRange([{ ...range[0], startDate: newDate }]);
                        }}
                      />
                    </div>
                    <div>
                      <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                      <input
                        type="date"
                        id="endDate"
                        className={inputClass}
                        value={formatDateForInput(range[0].endDate)}
                        min={formatDateForInput(range[0].startDate)}
                        max={formatDateForInput(maxDate)}
                        onChange={(e) => {
                          const newDate = e.target.value ? new Date(e.target.value) : addDays(new Date(), 7);
                          setRange([{ ...range[0], endDate: newDate }]);
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  <DateRange
                    months={1}
                    direction="horizontal"
                    editableDateInputs={true}
                    onChange={(item) => setRange([item.selection])}
                    moveRangeOnFirstSelection={false}
                    ranges={range}
                    minDate={today} // Prevent past dates
                    maxDate={maxDate} // Limit to 90 days in future
                  />
                )}
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm space-y-4">
                <h3 className="text-xl font-bold text-blue-700 flex items-center gap-3">
                  <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M8 2a1 1 0 00-1 1v1h6V3a1 1 0 10-2 0v1H9V3a1 1 0 00-1-1zM4 7v10a2 2 0 002 2h8a2 2 0 002-2V7H4z" />
                  </svg>
                  Selected Schedule
                </h3>
                <p className="text-gray-700 text-base">
                  <span className="font-semibold text-blue-800">Start Date:</span><br />
                  {range[0].startDate.toLocaleDateString("en-GB", {
                    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
                  })}
                </p>
                <p className="text-gray-700 text-base">
                  <span className="font-semibold text-blue-800">End Date:</span><br />
                  {range[0].endDate.toLocaleDateString("en-GB", {
                    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
                  })}
                </p>
                <p className="text-gray-700 text-base">
                  <span className="font-semibold text-blue-800">Duration:</span><br />
                  {
                    Math.ceil((range[0].endDate - range[0].startDate) / (1000 * 60 * 60 * 24)) + 1
                  } days
                </p>
              </div>
            </div>

            <input
              type="hidden"
              {...register("photoshootDate")}
              value={`${range[0].startDate.toLocaleDateString("en-GB")} — ${range[0].endDate.toLocaleDateString("en-GB")}`}
            />
          </div>
        )}

        {/* Digital Marketing Section */}
        {/* +++ NEW: Title row with "Create Custom" button +++ */}
        <div className="col-span-full flex justify-between items-center">
          <h2 className={sectionTitle} style={{ marginTop: 0, marginBottom: 0, border: 'none', paddingBottom: 0 }}>Digital Marketing</h2>
          <button
            type="button"
            onClick={() => setModalInfo({ isOpen: true, category: 'marketing' })}
            className="py-2 px-4 bg-blue-100 text-blue-700 font-semibold rounded-lg hover:bg-blue-200 transition-all text-sm"
          >
            + Create Custom
          </button>
        </div>
        <div className="col-span-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 px-2 py-1">
          {serviceOptions.marketing.map((item) => (
            <div key={item.id} className="p-5 border rounded-xl bg-white shadow-sm hover:shadow-md transition-all border-gray-300">
              <h4 className="text-lg font-semibold text-blue-800 mb-2">{item.label}</h4>
              <div className="space-y-3">
                {item.plans.map((plan, i) => {
                  const planLabel = `${item.label} (${plan.type})`;
                  // --- UPDATED: Check includes is_custom flag ---
                  const currentService = selectedServices.find(
                    (s) => s.label === planLabel && !s.is_custom
                  );
                  const isSelected = !!currentService;

                  return (
                    <div key={i}>
                      <label
                        onClick={() => {
                          if (isSelected) {
                            setSelectedServices((prev) => prev.filter((s) => s.label !== planLabel));
                          } else {
                            const filtered = selectedServices.filter(
                              (s) => s.category !== 'marketing' // Single select logic
                            );
                            // +++ NEW: Add category, is_custom, and plan_type +++
                            const newItem = {
                              ...item,
                              label: planLabel,
                              price: plan.price,
                              selectedAddons: [],
                              category: 'marketing',
                              is_custom: false,
                              plan_type: plan.type, // Store the plan type
                            };
                            setSelectedServices([...filtered, newItem]);
                          }
                        }}
                        className={`flex items-center justify-between p-3 rounded-lg cursor-pointer border transition duration-200 ${isSelected ? "bg-blue-50 border-blue-600 shadow" : "bg-white border-gray-300"
                          }`}
                      >
                        <span className="font-medium text-gray-800">{plan.type}</span>
                        <span className="font-semibold text-blue-800">₹{plan.price.toLocaleString("en-IN")}</span>
                        {isSelected && <span className="ml-2 text-green-600 font-bold">✓</span>}
                      </label>

                      {isSelected && item.addons?.length > 0 && (
                        <div className="pl-4 mt-2 space-y-1">
                          {item.addons.map((addon) => (
                            <label key={addon.id} className="flex items-center justify-between bg-yellow-50 p-2 border border-yellow-200 rounded-md cursor-pointer hover:bg-yellow-100 transition">
                              <div className="flex items-center space-x-2">
                                <input
                                  type="checkbox"
                                  className="accent-yellow-600"
                                  checked={currentService.selectedAddons?.some((a) => a.id === addon.id) || false}
                                  onChange={(e) => {
                                    setSelectedServices((prev) =>
                                      prev.map((s) => {
                                        if (s.label === planLabel) {
                                          const updatedAddons = e.target.checked
                                            // +++ NEW: Add is_custom flag to addon +++
                                            ? [...(s.selectedAddons || []), { ...addon, is_custom: false }]
                                            : (s.selectedAddons || []).filter((a) => a.id !== addon.id);
                                          return { ...s, selectedAddons: updatedAddons };
                                        }
                                        return s;
                                      })
                                    );
                                  }}
                                />
                                <span className="text-sm text-gray-800">{addon.name}</span>
                              </div>
                              <span className="text-sm font-semibold text-gray-700">₹{addon.price.toLocaleString("en-IN")}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* +++ NEW: Show custom Marketing services +++ */}
        {selectedServices.filter(s => s.category === 'marketing' && s.is_custom).map((item, idx) => (
            <div key={`custom-mktg-${idx}`} className="col-span-full sm:col-span-1 lg:col-span-1">
                <div className="relative p-5 rounded-xl border-2 text-base font-medium bg-blue-50 border-blue-600 shadow-md">
                    {item.label} ({item.plan_type}) – ₹{item.price.toLocaleString('en-IN')}
                    <span className="absolute top-2 right-3 text-xs text-blue-600 font-semibold">CUSTOM</span>
                    <button
                        type="button"
                        onClick={() => setSelectedServices(prev => prev.filter(s => s.label !== item.label))}
                        className="absolute top-1/2 -translate-y-1/2 right-3 text-red-500 hover:text-red-700 text-2xl font-bold"
                    >
                        &times;
                    </button>
                </div>
            </div>
        ))}


        {/* Website Creation Section */}
        {/* +++ NEW: Title row with "Create Custom" button +++ */}
        <div className="col-span-full flex justify-between items-center">
          <h2 className={sectionTitle} style={{ marginTop: 0, marginBottom: 0, border: 'none', paddingBottom: 0 }}>Website Creation</h2>
          <button
            type="button"
            onClick={() => setModalInfo({ isOpen: true, category: 'website' })}
            className="py-2 px-4 bg-blue-100 text-blue-700 font-semibold rounded-lg hover:bg-blue-200 transition-all text-sm"
          >
            + Create Custom
          </button>
        </div>
        <div className="col-span-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 overflow-hidden px-2 py-1">
          {serviceOptions.website.map((item, idx) => {
            // --- UPDATED: Check includes is_custom flag ---
            const currentService = selectedServices.find(
              (s) => s.id === item.id && !s.is_custom
            );
            const isSelected = !!currentService;

            return (
              <div key={idx} className="relative p-4 rounded-xl border border-gray-300 bg-white shadow-sm hover:shadow-md transition-all flex flex-col">
                <div className="flex-grow">
                  <h3 className="font-semibold text-lg mb-2">{item.label}</h3>
                  <p className="text-gray-600 mb-4">Base Price: ₹{item.price.toLocaleString('en-IN')}</p>

                  {item.addons && item.addons.length > 0 && (
                    <div className="space-y-2 mb-4">
                      {item.addons.map((addon) => (
                        <label key={addon.id} className={`flex items-center gap-2 text-sm ${isSelected ? 'cursor-pointer' : 'cursor-not-allowed text-gray-400'}`}>
                          <input
                            type="checkbox"
                            className="accent-blue-600" // Changed color
                            checked={currentService?.selectedAddons?.some((a) => a.id === addon.id) || false}
                            disabled={!isSelected}
                            onChange={(e) => {
                              setSelectedServices((prev) =>
                                prev.map((s) => {
                                  // --- UPDATED: Check for is_custom ---
                                  if (s.id === item.id && !s.is_custom) {
                                    const updatedAddons = e.target.checked
                                      // +++ NEW: Add is_custom flag to addon +++
                                      ? [...(s.selectedAddons || []), { ...addon, is_custom: false }]
                                      : (s.selectedAddons || []).filter((a) => a.id !== addon.id);
                                    return { ...s, selectedAddons: updatedAddons };
                                  }
                                  return s;
                                })
                              );
                            }}
                          />
                          <span>{addon.name} – ₹{addon.price.toLocaleString('en-IN')}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                <div
                  onClick={() => {
                    if (isSelected) {
                      setSelectedServices((prev) => prev.filter((s) => s.id !== item.id));
                    } else {
                      // +++ NEW: Add category and is_custom flag +++
                      // This category allows multi-select
                      setSelectedServices([...selectedServices, { ...item, selectedAddons: [], category: 'website', is_custom: false }]);
                    }
                  }}
                  className={`cursor-pointer p-3 mt-auto rounded-lg border-2 font-medium text-center transition-all duration-300 ${isSelected ? "bg-blue-50 border-blue-600 text-blue-800" : "bg-white border-gray-300 hover:border-blue-400"
                    }`}
                >
                  {isSelected ? "Selected ✓" : "Select Service"}
                </div>
              </div>
            );
          })}
        </div>
        
        {/* +++ NEW: Show custom Website services +++ */}
        {selectedServices.filter(s => s.category === 'website' && s.is_custom).map((item, idx) => (
            <div key={`custom-web-${idx}`} className="col-span-full sm:col-span-1 lg:col-span-1">
                <div className="relative p-5 rounded-xl border-2 text-base font-medium bg-blue-50 border-blue-600 shadow-md">
                    {item.label} – ₹{item.price.toLocaleString('en-IN')}
                    <span className="absolute top-2 right-3 text-xs text-blue-600 font-semibold">CUSTOM</span>
                    <button
                        type="button"
                        onClick={() => setSelectedServices(prev => prev.filter(s => s.label !== item.label))}
                        className="absolute top-1/2 -translate-y-1/2 right-3 text-red-500 hover:text-red-700 text-2xl font-bold"
                    >
                        &times;
                    </button>
                </div>
            </div>
        ))}


        {/* Conditional Rendering of Invoice Summary and Payment Section */}
        {selectedServices.length > 0 && (
          <>

            {/* Invoice Summary Section */}
            {/* This entire section is unchanged and will work correctly */}
            <div className="col-span-full w-full mt-12 bg-white border border-blue-200 rounded-2xl shadow-xl p-4 sm:p-8">
              <h2 className="text-3xl font-bold text-blue-800 mb-6 border-b-2 border-blue-100 pb-3">
                Invoice Summary
              </h2>

              {/* ================================================================== */}
              {/* DESKTOP VIEW: TABLE (Visible on medium screens and up)            */}
              {/* ================================================================== */}
              <div className="overflow-x-auto hidden md:block">
                <table className="min-w-full text-base text-left border border-gray-200 rounded-lg overflow-hidden">
                  <thead className="bg-blue-100 text-blue-800 font-semibold">
                    <tr>
                      <th className="px-5 py-3 border-b border-gray-200">#</th>
                      <th className="px-5 py-3 border-b border-gray-200">Service / Add-on</th>
                      <th className="px-5 py-3 border-b border-gray-200 text-right">Price (₹)</th>
                      <th className="px-5 py-3 border-b border-gray-200 text-right">SGST 9% (₹)</th>
                      <th className="px-5 py-3 border-b border-gray-200 text-right">CGST 9% (₹)</th>
                      <th className="px-5 py-3 border-b border-gray-200 text-right">Final (₹)</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-700">
                    {selectedServices.map((item, idx) => {
                      const sgst = item.price * 0.09;
                      const cgst = item.price * 0.09;
                      const final = item.price * 1.18;
                      return (
                        <React.Fragment key={`service-row-${idx}`}>
                          {/* Main Service Row */}
                          <tr className="even:bg-blue-50/50 hover:bg-blue-100/70 transition-colors duration-200">
                            <td className="px-5 py-3 border-b border-gray-200 font-bold">{idx + 1}</td>
                            <td className="px-5 py-3 border-b border-gray-200 font-bold">{item.label} {item.plan_type ? `(${item.plan_type})` : ''}</td>
                            <td className="px-5 py-3 border-b border-gray-200 text-right">{item.price.toLocaleString("en-IN")}</td>
                            <td className="px-5 py-3 border-b border-gray-200 text-right">{Math.round(sgst).toLocaleString("en-IN")}</td>
                            <td className="px-5 py-3 border-b border-gray-200 text-right">{Math.round(cgst).toLocaleString("en-IN")}</td>
                            <td className="px-5 py-3 border-b border-gray-200 text-right font-bold text-blue-800">{Math.round(final).toLocaleString("en-IN")}</td>
                          </tr>
                          {/* Add-on Rows */}
                          {item.selectedAddons?.map((addon, j) => {
                            const finalAddon = addon.price;
                            const basePriceAddon = finalAddon / 1.18;
                            const sgstAddon = basePriceAddon * 0.09;
                            const cgstAddon = basePriceAddon * 0.09;
                            return (
                              <tr key={`addon-row-${idx}-${j}`} className="bg-yellow-50 hover:bg-yellow-100 transition-colors duration-200 text-sm">
                                <td className="px-5 py-2 border-b border-yellow-200"></td>
                                <td className="px-5 py-2 border-b border-yellow-200 pl-8 text-gray-700">↳ {addon.name}</td>
                                <td className="px-5 py-2 border-b border-yellow-200 text-right">{Math.round(basePriceAddon).toLocaleString("en-IN")}</td>
                                <td className="px-5 py-2 border-b border-yellow-200 text-right">{Math.round(sgstAddon).toLocaleString("en-IN")}</td>
                                <td className="px-5 py-2 border-b border-yellow-200 text-right">{Math.round(cgstAddon).toLocaleString("en-IN")}</td>
                                <td className="px-5 py-2 border-b border-yellow-200 text-right font-semibold text-yellow-800">{finalAddon.toLocaleString("en-IN")}</td>
                              </tr>
                            );
                          })}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="bg-blue-200 text-blue-900 font-extrabold text-lg">
                      <td colSpan={5} className="px-5 py-4 border-t-2 border-blue-300 text-right">Total Payable</td>
                      <td className="px-5 py-4 border-t-2 border-blue-300 text-right">
                        ₹{grandTotal.toLocaleString('en-IN')}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* ================================================================== */}
              {/* MOBILE VIEW: CARDS (Visible on small screens only)               */}
              {/* ================================================================== */}
              <div className="space-y-4 md:hidden">
                {selectedServices.map((item, idx) => {
                  const sgst = item.price * 0.09;
                  const cgst = item.price * 0.09;
                  const final = item.price * 1.18;
                  return (
                    <div key={`card-${idx}`} className="bg-white border border-gray-200 rounded-lg p-4 shadow-md space-y-3">
                      {/* --- Main Service Card --- */}
                      <div className="font-bold text-lg text-blue-800 pb-2 border-b border-blue-100">
                        {idx + 1}. {item.label} {item.plan_type ? `(${item.plan_type})` : ''}
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between"><span className="text-gray-600">Price</span> <span>₹{item.price.toLocaleString("en-IN")}</span></div>
                        <div className="flex justify-between"><span className="text-gray-600">SGST 9%</span> <span>₹{Math.round(sgst).toLocaleString("en-IN")}</span></div>
                        <div className="flex justify-between"><span className="text-gray-600">CGST 9%</span> <span>₹{Math.round(cgst).toLocaleString("en-IN")}</span></div>
                        <div className="flex justify-between font-bold pt-1 border-t"><span className="text-blue-700">Subtotal</span> <span className="text-blue-700">₹{Math.round(final).toLocaleString("en-IN")}</span></div>
                      </div>

                      {/* --- Add-on Sub-Cards --- */}
                      {item.selectedAddons?.length > 0 && (
                        <div className="space-y-2 pt-3 mt-3 border-t border-dashed">
                          {item.selectedAddons.map((addon, j) => {
                            const finalAddon = addon.price;
                            const basePriceAddon = finalAddon / 1.18;
                            const sgstAddon = basePriceAddon * 0.09;
                            const cgstAddon = basePriceAddon * 0.09;
                            return (
                              <div key={`addon-card-${idx}-${j}`} className="bg-yellow-50 p-3 rounded-md text-sm">
                                <div className="font-semibold text-yellow-800 mb-2">↳ {addon.name}</div>
                                <div className="space-y-1 text-xs">
                                  <div className="flex justify-between"><span className="text-gray-600">Price</span> <span>₹{Math.round(basePriceAddon).toLocaleString("en-IN")}</span></div>
                                  <div className="flex justify-between"><span className="text-gray-600">GST 18%</span> <span>₹{Math.round(sgstAddon + cgstAddon).toLocaleString("en-IN")}</span></div>
                                  <div className="flex justify-between font-bold pt-1 border-t"><span className="text-yellow-700">Add-on Total</span> <span className="text-yellow-700">₹{finalAddon.toLocaleString("en-IN")}</span></div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )
                })}
                {/* --- Mobile Total --- */}
                <div className="flex justify-between items-center bg-blue-200 text-blue-900 font-extrabold text-lg p-4 rounded-lg mt-6">
                  <span>Total Payable</span>
                  <span>₹{grandTotal.toLocaleString('en-IN')}</span>
                </div>
              </div>
            </div>

            {/* PAYMENT TYPE SECTION (Unchanged) */}
            <div className="col-span-full w-full mt-12">
              {/* Payment Type Section */}

              <div className="col-span-full bg-green-50 border border-green-300 p-6 rounded-xl shadow-inner mb-10">
                <h2 className="text-xl font-bold text-green-800 mb-4">1. Select Payment Amount *</h2>
                <div className="flex flex-wrap gap-6">
                  <label className={`flex items-center gap-3 p-4 border rounded-lg cursor-pointer transition-all duration-200 shadow-sm
                    ${watch("payment_status") === "Complete" ? "border-green-600 bg-white" : "border-green-300 bg-green-100 hover:bg-green-200"}`}>
                    <input
                      type="radio"
                      value="Complete"
                      {...register("payment_status", { required: "Payment status is required." })}
                      className="accent-green-600 w-5 h-5"
                    />
                    <span className="text-green-800 font-semibold text-lg">Complete</span>
                  </label>

                  <label className={`flex items-center gap-3 p-4 border rounded-lg cursor-pointer transition-all duration-200 shadow-sm
                    ${watch("payment_status") === "Partial" ? "border-green-600 bg-white" : "border-green-300 bg-green-100 hover:bg-green-200"}`}>
                    <input
                      type="radio"
                      value="Partial"
                      {...register("payment_status", { required: "Payment status is required." })}
                      className="accent-green-600 w-5 h-5"
                    />
                    <span className="text-green-800 font-semibold text-lg">Partial</span>
                  </label>
                </div>
                {errors.payment_status && <p className={`${errorTextClass} mt-2`}>{errors.payment_status.message}</p>}


                {watch("payment_status") === "Partial" && (
                  <div className="mt-6">
                    <label className="block text-base font-medium text-green-900 mb-2">
                      Enter Partial Amount <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text" // Use "text" to allow commas
                      inputMode="numeric" // Improves mobile keyboard experience
                      placeholder="Enter partial payment amount"
                      value={formattedPartialAmount} // Display the formatted value
                      onChange={handlePartialAmountChange} // Use the custom handler
                      className={`${inputClass} ${errors.partialAmount ? errorInputClass : ''} ${!errors.partialAmount && dirtyFields.partialAmount ? successInputClass : ''}`}
                    />
                    {errors.partialAmount && <p className={errorTextClass}>{errors.partialAmount.message}</p>}
                  </div>
                )}
              </div>
              <div className="col-span-full bg-blue-50 border border-blue-300 p-6 rounded-xl shadow-inner mb-10">
                <h2 className="text-xl font-bold text-blue-800 mb-4">2. Select Payment Method *</h2>
                <div className="flex flex-wrap gap-6">
                  {/* Online */}
                  <label className={`flex items-center gap-3 p-4 border rounded-lg cursor-pointer transition-all duration-200 shadow-sm
                    ${paymentType === "Online" ? "border-blue-600 bg-white" : "border-blue-300 bg-blue-100 hover:bg-blue-200"}`}>
                    <input
                      type="radio"
                      value="Online"
                      {...register("paymentType", { required: "Payment method is required." })}
                      className="accent-blue-600 w-5 h-5"
                    />
                    <span className="text-blue-800 font-semibold text-lg">Online</span>
                  </label>

                  {/* Cheque */}
                  <label className={`flex items-center gap-3 p-4 border rounded-lg cursor-pointer transition-all duration-200 shadow-sm
                    ${paymentType === "Cheque" ? "border-blue-600 bg-white" : "border-blue-300 bg-blue-100 hover:bg-blue-200"}`}>
                    <input
                      type="radio"
                      value="Cheque"
                      {...register("paymentType", { required: "Payment method is required." })}
                      className="accent-blue-600 w-5 h-5"
                    />
                    <span className="text-blue-800 font-semibold text-lg">Cheque</span>
                  </label>

                  {/* Others */}
                  <label className={`flex items-center gap-3 p-4 border rounded-lg cursor-pointer transition-all duration-200 shadow-sm
                    ${paymentType === "Others" ? "border-blue-600 bg-white" : "border-blue-300 bg-blue-100 hover:bg-blue-200"}`}>
                    <input
                      type="radio"
                      value="Others"
                      {...register("paymentType", { required: "Payment method is required." })}
                      className="accent-blue-600 w-5 h-5"
                    />
                    <span className="text-blue-800 font-semibold text-lg">Others</span>
                  </label>
                </div>

                {errors.paymentType && (
                  <p className={`${errorTextClass} mt-2`}>
                    {errors.paymentType.message}
                  </p>
                )}
              </div>

              {/* CHEQUE DETAILS (Unchanged) */}
              {paymentType === "Cheque" && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 p-6 bg-gray-50 border border-gray-200 rounded-xl shadow-inner">
                  <div>
                    <input placeholder="Cheque Number *" {...register("chequeNumber", { required: "Cheque number is required." })} className={`${inputClass} ${errors.chequeNumber ? errorInputClass : ''} ${!errors.chequeNumber && dirtyFields.chequeNumber ? successInputClass : ''}`} />
                    {errors.chequeNumber && <p className={errorTextClass}>{errors.chequeNumber.message}</p>}
                  </div>
                  <div>
                    <input placeholder="Cheque Date (DD/MM/YYYY) *" {...register("chequeDate", { required: "Cheque date is required.", pattern: { value: /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/, message: "Date must be in DD/MM/YYYY format." } })} className={`${inputClass} ${errors.chequeDate ? errorInputClass : ''} ${!errors.chequeDate && dirtyFields.chequeDate ? successInputClass : ''}`} />
                    {errors.chequeDate && <p className={errorTextClass}>{errors.chequeDate.message}</p>}
                  </div>
                  <div>
                    <input placeholder="Bank Name *" {...register("bankName", { required: "Bank name is required." })} className={`${inputClass} ${errors.bankName ? errorInputClass : ''} ${!errors.bankName && dirtyFields.bankName ? successInputClass : ''}`} />
                    {errors.bankName && <p className={errorTextClass}>{errors.bankName.message}</p>}
                  </div>
                  <div>
                    <input placeholder="Branch *" {...register("branch", { required: "Branch is required." })} className={`${inputClass} ${errors.branch ? errorInputClass : ''} ${!errors.branch && dirtyFields.branch ? successInputClass : ''}`} />
                    {errors.branch && <p className={errorTextClass}>{errors.branch.message}</p>}
                  </div>

                  <div>
                    <input
                      placeholder="Amount *"
                      {...register("totalAmount", { required: "Amount is required." })}
                      className={`${inputClass} ${errors.totalAmount ? errorInputClass : ''} bg-gray-200 cursor-not-allowed`}
                      readOnly
                    />
                    {errors.totalAmount && <p className={errorTextClass}>{errors.totalAmount.message}</p>}
                  </div>
                </div>
              )}

              {/* ONLINE PAYMENT DETAILS (Unchanged) */}
              {paymentType === "Online" && (
                <div className="grid grid-cols-1 gap-6 p-6 bg-gray-50 border border-gray-200 rounded-xl shadow-inner">
                  <div>
                    <input placeholder="Transaction ID *" {...register("transactionId", { required: "Transaction ID is required." })} className={`${inputClass} col-span-full ${errors.transactionId ? errorInputClass : ''} ${!errors.transactionId && dirtyFields.transactionId ? successInputClass : ''}`} />
                    {errors.transactionId && <p className={errorTextClass}>{errors.transactionId.message}</p>}
                  </div>
                  <div>
                    <label className="block text-gray-700 font-medium text-base mb-2">Upload Payment Screenshot *</label>
                    <input type="file" {...register("screenshot", { required: "Screenshot is required." })} className={`${inputClass} col-span-full file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-100 file:text-blue-700 hover:file:bg-blue-200 ${errors.screenshot ? errorInputClass : ''}`} />
                    {errors.screenshot && <p className={errorTextClass}>{errors.screenshot.message}</p>}
                  </div>

                  <div className="mt-8 bg-blue-50 border border-blue-200 p-8 rounded-xl shadow-lg">
                    <div className="flex items-center gap-4 mb-6">
                      <svg className="w-8 h-8 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9 2a1 1 0 00-1 1v1H6a2 2 0 00-2 2v1h12V6a2 2 0 00-2-2h-2V3a1 1 0 00-1-1H9zM4 9v7a2 2 0 002 2h8a2 2 0 002-2V9H4zm2 2h2v2H6v-2zm4 0h2v2h-2v-2z" />
                      </svg>
                      <h3 className="text-2xl font-bold text-blue-900 overflow-hidden">Scan QR for Payment</h3>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 items-center">
                      <img
                        src={Alsol_QR}
                        alt="Alsol QR Code"
                        className="w-56 h-56 object-contain mx-auto sm:mx-0 border-4 border-gray-300 rounded-xl shadow-md"
                      />
                      <div className="text-base text-gray-800 space-y-3">
                        <p><strong>Account No:</strong> 50200057603611</p>
                        <p><strong>IFSC:</strong> HDFC0000718</p>
                        <p><strong>Account Type:</strong> Current</p>
                        <p><strong>Beneficiary:</strong> Alsol Technology Solution Pvt Ltd</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* Other Details Section (Unchanged) */}
        <h2 className={sectionTitle}>Other Details</h2>

        <div className="col-span-1">
          <input placeholder="GSTIN No." {...register("gstin", { pattern: { value: /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/, message: "Invalid GSTIN format (e.g., 29ABCDE1234F1Z5)." } })} className={`${inputClass} ${errors.gstin ? errorInputClass : ''} ${!errors.gstin && dirtyFields.gstin ? successInputClass : ''}`} />
          {errors.gstin && <p className={errorTextClass}>{errors.gstin.message}</p>}
        </div>
        <div className="col-span-1">
          <input placeholder="PAN No." {...register("pan", { pattern: { value: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/, message: "Invalid PAN format (e.g., ABCDE1234F)." } })} className={`${inputClass} ${errors.pan ? errorInputClass : ''} ${!errors.pan && dirtyFields.pan ? successInputClass : ''}`} />
          {errors.pan && <p className={errorTextClass}>{errors.pan.message}</p>}
        </div>

        <div className="col-span-1">
          <select
            {...register("companyType")}
            className={`${inputClass} bg-white`}
            onChange={(e) => setCompanyType(e.target.value)}
            value={companyType}
          >
            <option value="">-- Select Company Type --</option>
            <option value="Entrepreneurship">Entrepreneurship</option>
            <option value="Partnership">Partnership</option>
            <option value="Private Limited">Private Limited</option>
            <option value="Others">Others</option>
          </select>
        </div>

        {companyType === "Others" && (
          <div className="col-span-1">
            <input
              placeholder="Please specify your company type"
              {...register("customCompanyType", { required: companyType === 'Others' })}
              className={`${inputClass} ${!errors.customCompanyType && dirtyFields.customCompanyType ? successInputClass : ''}`}
            />
          </div>
        )}

        <div className="col-span-1">
          <input placeholder="ASSOCIATE CODE" {...register("bdmCode")} className={inputClass} />
        </div>

        {/* Submit Button (Unchanged) */}
        <button
          type="submit"
          disabled={isSubmitting}
          className="col-span-full bg-blue-700 hover:bg-blue-800 text-white font-semibold py-4 px-8 rounded-xl mt-8 transition-all duration-300 ease-in-out transform hover:scale-105 shadow-lg disabled:bg-gray-400 disabled:cursor-not-allowed disabled:scale-100"
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center overflow-hidden">
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Submitting...
            </span>
          ) : (
            'Submit Application'
          )}
        </button>
      </form>
    </div>
  );
}